import _ from 'lodash';
import {
  createReducer,
  createFetchReducers,
  createFetchSelectors,
  createFetchActionForNamespace
} from 'utils/reducers';

export const LOGIN_REQUEST = 'auth/LOGIN_REQUEST';
export const LOGIN_ERROR = 'auth/LOGIN_ERROR';
export const LOGIN_SUCCESS = 'auth/LOGIN_SUCCESS';
export const LOGOUT_REQUEST = 'auth/LOGOUT_REQUEST';

export const USER_PROFILE_REQUEST = 'auth/USER_PROFILE_REQUEST';
export const USER_PROFILE_SUCCESS = 'auth/USER_PROFILE_SUCCESS';
export const CHANGE_USER_PASSWORD_AND_LOGIN =
  'auth/CHANGE_USER_PASSWORD_AND_LOGIN';
export const CHANGE_USER_PASSWORD_AND_LOGIN_FAILURE =
  'auth/CHANGE_USER_PASSWORD_AND_LOGIN_FAILURE';
export const ADMIN_FORGOT_PASSWORD = 'auth/ADMIN_FORGOT_PASSWORD';
export const ADMIN_FORGOT_PASSWORD_SUCCESS =
  'auth/ADMIN_FORGOT_PASSWORD_SUCCESS';
export const ADMIN_FORGOT_PASSWORD_FAILURE =
  'auth/ADMIN_FORGOT_PASSWORD_FAILURE';
export const ADMIN_FORGOT_PASSWORD_SET = 'auth/ADMIN_FORGOT_PASSWORD_SET';
export const ADMIN_FORGOT_PASSWORD_SET_SUCCESS =
  'auth/ADMIN_FORGOT_PASSWORD_SET_SUCCESS';
export const ADMIN_FORGOT_PASSWORD_SET_FAILURE =
  'auth/ADMIN_FORGOT_PASSWORD_SET_FAILURE';
export const SET_FORGOT_PASSWORD_TOKEN = 'auth/SET_FORGOT_PASSWORD_TOKEN';

export const REQUIRES_PERMISSION = 'auth/REQUIRES_PERMISSION';
export const REQUIRES_MULTIPLE_PERMISSIONS =
  'auth/REQUIRES_MULTIPLE_PERMISSIONS';
export const REQUIRES_PERMISSION_OR_TEAM_OWNERSHIP =
  'auth/REQUIRES_PERMISSION_OR_TEAM_OWNERSHIP';
export const REDIRECT_TO_DEFAULT_PAGE = 'auth/REDIRECT_TO_DEFAULT_PAGE';

const createFetchAction = createFetchActionForNamespace('auth');

export const requiresPermission = (
  {
    locationRef,
    permission,
    disableClose
  }: {locationRef?: string; permission: string; disableClose?: boolean},
  hasPermissionFunc = () => {}
) => ({
  type: REQUIRES_PERMISSION,
  payload: {locationRef, permission, disableClose, hasPermissionFunc}
});

export const requiresMultiplePermissions = (
  {permissions, disableClose},
  hasPermissionFunc = () => {}
) => ({
  type: REQUIRES_MULTIPLE_PERMISSIONS,
  payload: {permissions, disableClose, hasPermissionFunc}
});

export const requiresPermissionOrTeamOwnership = (
  {locationRef, permission, teamId},
  hasPermissionFunc = () => {}
) => ({
  type: REQUIRES_PERMISSION_OR_TEAM_OWNERSHIP,
  payload: {locationRef, permission, teamId, hasPermissionFunc}
});

export const redirectToDefaultPage = ({locationRef}) => ({
  type: REDIRECT_TO_DEFAULT_PAGE,
  payload: {locationRef}
});

export const loginRequest = ({email, password, recaptcha}) => ({
  type: LOGIN_REQUEST,
  payload: {
    email,
    password,
    recaptcha
  }
});

export const logoutRequest = (logoutMessage?: string) => ({
  type: LOGOUT_REQUEST,
  payload: {logoutMessage}
});

export const loginError = errors => ({
  type: LOGIN_ERROR,
  error: true,
  payload: {
    errors
  }
});

export const loginSuccess = () => ({
  type: LOGIN_SUCCESS
});

export const userProfileRequest = () => ({
  type: USER_PROFILE_REQUEST,
  payload: {}
});

export const userProfileSuccess = me => ({
  type: USER_PROFILE_SUCCESS,
  payload: {
    me
  }
});

export const fetchUserByInvitationKey = createFetchAction({
  name: 'FETCH_USER_BY_INVITATION_KEY',
  initialAction: ({key}, extraActions = []) => ({
    payload: {key, extraActions}
  }),
  stateKey: 'invitationUser',
  defaultStateValue: {}
});
const fetchUserByInvitationKeySelector = createFetchSelectors(
  fetchUserByInvitationKey
);

export const selectInvitationUser = fetchUserByInvitationKeySelector.data;

export const selectInvitationErrors = fetchUserByInvitationKeySelector.errors;

export const selectInvitationUserErrors = state =>
  _.get(selectInvitationErrors(state), '_error');

export const changeUserPasswordAndLogin = ({email, key, password}) => ({
  type: CHANGE_USER_PASSWORD_AND_LOGIN,
  payload: {
    email,
    key,
    password
  }
});

export const changeUserPasswordAndLoginFailure = errors => ({
  type: CHANGE_USER_PASSWORD_AND_LOGIN_FAILURE,
  payload: {
    errors
  }
});

export const adminForgotPassword = ({email}) => ({
  type: ADMIN_FORGOT_PASSWORD,
  payload: {email}
});

export const adminForgotPasswordSuccess = () => ({
  type: ADMIN_FORGOT_PASSWORD_SUCCESS,
  payload: {}
});

export const adminForgotPasswordFailure = errors => ({
  type: ADMIN_FORGOT_PASSWORD_FAILURE,
  payload: {errors}
});

export const adminForgotPasswordSet = ({token, password}) => ({
  type: ADMIN_FORGOT_PASSWORD_SET,
  payload: {token, password}
});

export const adminForgotPasswordSetSuccess = () => ({
  type: ADMIN_FORGOT_PASSWORD_SET_SUCCESS,
  payload: {}
});

export const adminForgotPasswordSetFailure = errors => ({
  type: ADMIN_FORGOT_PASSWORD_SET_FAILURE,
  payload: {errors}
});

export const setForgotPasswordToken = ({token}) => ({
  type: SET_FORGOT_PASSWORD_TOKEN,
  payload: {token}
});

export const START_INACTIVITY_LOGOUT_TIMER =
  'auth/START_INACTIVITY_LOGOUT_TIMER';
export const startInactivityLogoutTimer = forceLogoutAfter => ({
  type: START_INACTIVITY_LOGOUT_TIMER,
  payload: {forceLogoutAfter}
});
export const STOP_INACTIVITY_LOGOUT_TIMER = 'auth/STOP_INACTIVITY_LOGOUT_TIMER';
export const stopInactivityLogoutTimer = () => ({
  type: STOP_INACTIVITY_LOGOUT_TIMER,
  payload: {}
});

export const PERMISSIONS = {
  VIEW_BOOKING: 'viewBooking',
  CREATE_BOOKING: 'createBooking',
  CANCEL_BOOKING: 'cancelBooking',
  VIEW_OFFERS: 'viewOffers',
  CREATE_NEW_OFFER: 'createNewOffer',
  ADD_GUARANTOR: 'addGuarantor',
  REMOVE_GUARANTOR: 'removeGuarantor',
  ADD_SECONDARY_TENANT: 'addSecondaryTenant',
  REMOVE_SECONDARY_TENANT: 'removeSecondaryTenant',
  VIEW_MEMBER_INFORMATION: 'viewMemberInformation',
  VIEW_MEMBER_DETAILS: 'viewMemberDetails',
  EDIT_MEMBER_INFORMATION: 'editMemberInformation',
  VIEW_KYC_OVERVIEW: 'viewKycOverview',
  VIEW_KYC_DETAILED_VIEW: 'viewKycDetailedView',
  VIEW_VISA_KYC_SECTION: 'viewVisaKycSection',
  VIEW_POA_KYC_SECTION: 'viewPoaKycSection',
  EDIT_KYC: 'editKyc',
  VERIFY_KYC: 'verifyKyc',
  VIEW_AFFORDABILITY_OVERVIEW: 'viewAffordabilityOverview',
  ASSESS_AFFORDABILITY: 'assessAffordability',
  VIEW_EMPLOYMENT: 'viewEmployment',
  EDIT_EMPLOYMENT: 'editEmployment',
  CREATE_PAYMENT_SCHEDULE: 'createPaymentSchedule',
  APPLY_PAYMENT_SCHEDULE: 'applyPaymentSchedule',
  VIEW_AGREEMENT: 'viewAgreement',
  ADD_AGREEMENT: 'addAgreement',
  SEND_AGREEMENT: 'sendAgreement',
  CANCEL_AGREEMENT: 'cancelAgreement',
  SEND_AGREEMENT_REMINDER: 'sendAgreementReminder',
  DOWNLOAD_AGREEMENT: 'downloadAgreement',
  SIGN_AGREEMENT_MANUALLY: 'signAgreementManually',
  VIEW_LEGACY_DOCUMENTS: 'viewLegacyDocuments',
  UPLOAD_LEGACY_DOCUMENT: 'uploadLegacyDocument',
  EDIT_DELETE_LEGACY_DOCUMENT: 'editDeleteLegacyDocument',
  VIEW_ROOM_SCHEDULE: 'viewRoomSchedule',
  CREATE_ROOM_MOVE: 'createRoomMove',
  VIEW_RENT_SCHEDULE: 'viewRentSchedule',
  VIEW_SCHEDULE_ITEM_DETAILS: 'viewScheduleItemDetails',
  CREATE_INVOICE: 'createInvoice',
  ADD_PAYMENT: 'addPayment',
  INITIATE_GOCARDLESS_PAYMENT: 'initiateGocardlessPayment',
  VOID_SCHEDULE_ITEMS: 'voidScheduleItems',
  ADD_XERO_ID: 'addXeroId',
  SYNC_WITH_XERO: 'syncWithXero',
  CREATE_CREDIT_NOTE: 'createCreditNote',
  CREATE_OVERPAYMENT: 'createOverpayment',
  ADD_REFUND: 'addRefund',
  VIEW_ONE_OFF_CHARGES_SCHEDULE: 'viewOneOffChargesSchedule',
  VIEW_DEPOSIT_SCHEDULE: 'viewDepositSchedule',
  VIEW_EARLY_TERMINATION: 'viewEarlyTermination',
  CREATE_EARLY_TERMINATION: 'createEarlyTermination',
  VIEW_BREAK_CLAUSE: 'viewBreakClause',
  VIEW_ALLOCATIONS: 'viewAllocations',
  CREATE_ROOM: 'createRoom',
  ARCHIVE_ROOM: 'archiveRoom',
  UNDO_ARCHIVE_ROOM: 'undoArchiveRoom',
  VIEW_FRONT_DESK_DASHBOARD: 'viewFrontDeskDashboard',
  CREATE_CHECK_IN: 'createCheckIn',
  CREATE_CHECK_OUT: 'createCheckOut',
  COMPLETE_ROOM_MOVE: 'confirmRoomChange',
  EDIT_ROOM_INSPECTION: 'editRoomInspection',
  VIEW_ALL_TEAMS: 'viewAllTeams',
  VIEW_TEAM_DETAILS: 'viewTeamDetails',
  EDIT_TEAM_MEMBERS: 'editTeamMembers',
  REMOVE_TEAM_MEMBERS: 'removeTeamMembers',
  ADD_TEAM_MEMBERS: 'addTeamMembers',
  INVITE_NEW_TEAM_MEMBER: 'inviteNewTeamMember',
  EDIT_TEAM_DETAILS: 'editTeamDetails',
  EDIT_TEAM_PERMISSIONS: 'editTeamPermissions',
  DELETE_TEAM: 'deleteTeam',
  EDIT_TEAM_LOCATIONS: 'editTeamLocations',
  ADD_NEW_TEAM: 'addNewTeam',
  VIEW_STAFF_MEMBERS_LIST: 'viewStaffMembersList',
  VIEW_STAFF_MEMBERS_DETAILS: 'viewStaffMembersDetails',
  INVITE_NEW_STAFF_MEMBER: 'inviteNewStaffMember',
  EDIT_STAFF_MEMBER_DETAILS: 'editStaffMemberDetails',
  VIEW_BOOKING_NOTES: 'viewBookingNotes',
  CREATE_BOOKING_NOTE: 'createBookingNote',
  EDIT_BOOKING_NOTE: 'editBookingNote',
  VIEW_USERS: 'viewUsers',
  VIEW_USER_DETAIL: 'viewUserDetail',
  EDIT_USERS: 'editUsers',
  VIEW_LOCATION: 'viewLocation',
  VIEW_LOCATION_DETAIL: 'viewLocationDetail',
  VIEW_ROOM_NOTES: 'viewRoomNotes',
  EDIT_ROOM_NOTE: 'editRoomNote',
  CREATE_ROOM_NOTE: 'createRoomNote',
  CHANGE_ROOM_CLEANING_STATUS: 'changeRoomCleaningStatus',
  EXPORT_EMERGENCY_CONTACTS_LIST: 'exportEmergencyContactsList',
  VIEW_WEBHOOKS: 'viewWebhooks',
  VIEW_QUICKSEARCH: 'viewQuicksearch',
  VIEW_INVOICE_REPORTING: 'viewInvoiceReporting',
  VIEW_OCCUPANCY_DASHBOARD: 'viewOccupancyDashboard',
  EXPORT_OCCUPANCY_REPORT: 'exportOccupancyReport',
  VIEW_REVENUE_REPORTING: 'viewRevenueReporting',
  EXPORT_REVENUE_REPORTING: 'exportRevenueReporting',
  SETUP_GOCARDLESS: 'setupGocardless',
  CREATE_DELETE_OFFLINE_PERIOD: 'createDeleteOfflinePeriod',
  SET_TEAM_INACTIVITY_TIMER: 'setTeamInactivityTimer',
  VIEW_ROOMS_DASHBOARD: 'viewRoomsDashboard',
  RESOLVE_BOOKING_NOTE: 'resolveBookingNote',
  VIEW_NEIGHBOURS_DETAILS: 'viewNeighboursDetails',
  VIEW_NOTES_LIST: 'viewNotesList',
  VIEW_INTEGRATIONS_STATUS: 'viewIntegrationsStatus',
  VIEW_MEMBERS_LIST: 'viewMembersList',
  VIEW_MEMBER_PROFILE: 'viewMemberProfile',
  EXPORT_MEMBERS_LIST: 'exportMembersList',
  VIEW_PAYMENTS_LIST: 'viewPaymentsList',
  VIEW_CREDIT_NOTES_LIST: 'viewCreditNotesList',
  VIEW_ACTIVITY_LOG: 'viewActivityLog',
  VIEW_ONE_OFF_CHARGE_REASONS: 'viewOneOffChargeReasons',
  CREATE_CHARGE: 'createCharge',
  DEALLOCATE_CREDIT_NOTE: 'unallocateCreditNote',
  ADD_ROOM_CHECK: 'addRoomCheck',
  EDIT_ROOM_CHECK: 'editRoomCheck',
  EDIT_DEPOSIT_SUMMARY: 'editDepositSummary',
  RELEASE_FULL_DEPOSIT: 'releaseFullDeposit',
  RETAIN_FROM_DEPOSIT: 'retainFromDeposit',
  COMPLETE_ROOM_CHECK: 'completeRoomCheck',
  CREATE_FINANCE_ITEM_FOR_PAST_MONTHS: 'createFicanceItemForPastMonths',
  VIEW_BOOKING_TRACKER: 'viewBookingTracker',
  VIEW_CSV_EXPORT_FILE: 'viewCsvExportFile',
  VIEW_EXCEL_EXPORT_FILE: 'viewExcelExportFile',
  VIEW_ROOM_PURPOSES: 'viewRoomPurposes',
  CREATE_ROOM_PURPOSES: 'createRoomPurposes',
  APPROVE_DRAFT_INVOICE: 'approveDraftInvoice',
  DELETE_DRAFT_INVOICE: 'deleteDraftInvoice',
  VIEW_LOOKER_DASHBOARD: 'viewLookerDashboard'
};

const initialState = {
  loginErrors: [],
  loginWarnings: [],
  me: null,
  errors: null,
  forgotPasswordErrors: null,
  forgotPasswordSuccess: false,
  forgotPasswordToken: null,
  forgotPasswordSetSuccess: null,
  forgotPasswordSetErrors: null
};

const fetchReducer = createReducer(initialState, {
  ...createFetchReducers(fetchUserByInvitationKey)
});

const reducer = (state = initialState, action) => {
  const newState = fetchReducer(state, action);

  switch (action.type) {
    case LOGIN_ERROR:
      return {
        ...state,
        loginErrors: action.payload.errors
      };
    case LOGIN_SUCCESS:
      return {
        ...state,
        loginErrors: [],
        logoutMessage: undefined
      };
    case LOGOUT_REQUEST:
      return {
        ...state,
        logoutMessage: action.payload.logoutMessage
      };
    case USER_PROFILE_SUCCESS:
      return {
        ...state,
        me: action.payload.me
      };
    case ADMIN_FORGOT_PASSWORD_SUCCESS:
      return {
        ...state,
        forgotPasswordSuccess: true,
        forgotPasswordErrors: null
      };
    case ADMIN_FORGOT_PASSWORD_FAILURE:
      return {
        ...state,
        forgotPasswordErrors: action.payload.errors
      };
    case SET_FORGOT_PASSWORD_TOKEN:
      return {
        ...state,
        forgotPasswordToken: action.payload.token
      };
    case ADMIN_FORGOT_PASSWORD_SET_SUCCESS:
      return {
        ...state,
        forgotPasswordSetSuccess: true,
        forgotPasswordErrors: null
      };
    case ADMIN_FORGOT_PASSWORD_SET_FAILURE:
      return {
        ...state,
        forgotPasswordSetErrors: action.payload.errors
      };
    default:
      return newState;
  }
};

export default reducer;
export * from './saga';
export * from './selectors';
